// src/pages/FieldBox.js
import React, { useRef, useEffect } from 'react';
import { Info, Edit2, Save, Loader, XCircle } from 'lucide-react';
import { isURL } from './Productpassporthelpers';

// ── Moved outside FieldBox so it is never recreated on parent re-render ───────
function ValueDisplay({
  value, field, nestedKey, arrayIndex,
  editingField, editValue, saving,
  setEditValue, onSave, onCancel
}) {
  const inputRef = useRef(null);

  const isEditing =
    editingField &&
    editingField.apiKey     === field.apiKey   &&
    editingField.fieldKey   === field.fieldKey &&
    editingField.nestedKey  === (nestedKey  ?? null) &&
    editingField.arrayIndex === (arrayIndex ?? null);

  // Restore cursor position after every value change
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const pos = inputRef.current.selectionStart;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [editValue, isEditing]);

  if (isEditing) {
    return (
      <div className="space-y-2">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => {
            const pos = e.target.selectionStart;
            setEditValue(e.target.value);
            // Preserve cursor after state update
            requestAnimationFrame(() => {
              if (inputRef.current) {
                inputRef.current.setSelectionRange(pos, pos);
              }
            });
          }}
          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="Enter value"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onSave(); }}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-400 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50 text-sm"
          >
            <XCircle className="w-3 h-3" />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (isURL(value)) {
    return (
      <a
        href={value.startsWith('www.') ? `https://${value}` : value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-blue-600 font-medium break-all underline"
      >
        {value}
      </a>
    );
  }

  return <p className="text-sm text-gray-800 font-medium break-words">{value}</p>;
}

// ── EditButton ────────────────────────────────────────────────────────────────
function EditButton({ onClick, disabled }) {
  if (disabled) return null;
  return (
    <button
      onClick={onClick}
      className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors flex-shrink-0"
      title="Edit field"
    >
      <Edit2 className="w-3 h-3" />
    </button>
  );
}

// ── FieldBox ──────────────────────────────────────────────────────────────────
function FieldBox({
  field,
  editingField,
  editValue,
  saving,
  setEditValue,
  onEdit,
  onSave,
  onCancel
}) {
  const sharedProps = { editingField, editValue, saving, setEditValue, onSave, onCancel };

  // ── Nested field box ───────────────────────────────────────────────────────
  if (field.isNested) {
    return (
      <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="text-xs font-semibold text-gray-600 uppercase">{field.label}</span>
        </div>

        {field.isArray ? (
          <div className="space-y-3">
            {field.arrayItems.map((item, idx) => (
              <div key={idx} className="pl-4 border-l-2 border-blue-300">
                <p className="text-xs font-semibold text-gray-500 mb-1">{item.label}</p>
                <div className="space-y-2">
                  {item.nestedFields.map((nestedField, nIdx) => (
                    <div key={nIdx} className="pb-2 last:pb-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-xs text-gray-600">{nestedField.label}</p>
                        <EditButton
                          onClick={() => onEdit(field, nestedField.nestedKey, idx)}
                          disabled={!nestedField.isEditable}
                        />
                      </div>
                      <ValueDisplay
                        {...sharedProps}
                        value={nestedField.value}
                        field={field}
                        nestedKey={nestedField.nestedKey}
                        arrayIndex={idx}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2 pl-4 border-l-2 border-blue-200">
            {field.nestedFields.map((nestedField, idx) => (
              <div key={idx} className="pb-2 last:pb-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs text-gray-600">{nestedField.label}</p>
                  <EditButton
                    onClick={() => onEdit(field, nestedField.nestedKey)}
                    disabled={!nestedField.isEditable}
                  />
                </div>
                <ValueDisplay
                  {...sharedProps}
                  value={nestedField.value}
                  field={field}
                  nestedKey={nestedField.nestedKey}
                  arrayIndex={null}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Flat field box ─────────────────────────────────────────────────────────
  return (
    <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="text-xs font-medium text-gray-600">{field.label}</span>
        </div>
        <EditButton
          onClick={() => onEdit(field)}
          disabled={!field.isEditable}
        />
      </div>
      <ValueDisplay
        {...sharedProps}
        value={field.value}
        field={field}
        nestedKey={null}
        arrayIndex={null}
      />
    </div>
  );
}

export default FieldBox;